package com.klantenstop.web.utils

import com.klantenstop.web.data.models.domain.Category
import com.klantenstop.web.data.models.domain.Province
import com.klantenstop.web.data.models.domain.Radius
import com.klantenstop.web.data.models.domain.SearchParams

/**
 * Single source of truth for turning [SearchParams] into the "stad" slug sent to both the URL and
 * the backend. Prefers the authoritative [SearchParams.locationSlug] (e.g. a province's
 * disambiguated "-provincie" slug, see [Province.citySearchSlug]) over re-slugifying the
 * display-only [SearchParams.query], since the two can legitimately differ.
 */
fun SearchParams.toCitySlug(): String? {
    val rawLocation = locationSlug?.takeIf { it.isNotBlank() } ?: query.trim()
    return rawLocation.takeIf { it.isNotBlank() }?.toUrlSlug()?.takeIf { it.isNotBlank() }
}

/**
 * Reconstructs [SearchParams] from a URL/backend "stad" slug -- the inverse of [toCitySlug]. A
 * slug matching a province's [Province.citySearchSlug] (e.g. "antwerpen-provincie") resolves to
 * the clean province display name plus the original slug, instead of blindly reversing it with
 * [fromUrlSlugDisplay] (which would otherwise leak "Antwerpen-Provincie" into the search UI).
 */
fun searchParamsFromCitySlug(citySlug: String?, category: Category?, radius: Radius): SearchParams {
    val matchedProvince = citySlug?.let(Province::fromCitySearchSlug)
    return SearchParams(
        query = matchedProvince?.displayName ?: citySlug?.fromUrlSlugDisplay() ?: "",
        category = category,
        radius = radius,
        locationSlug = citySlug
    )
}
